import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HeartHandshake, Lock, Mail } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { reportarErrorInesperado, detalleTecnico } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from '../components/ui/FormErrorAlert';

// Mensajes con que Supabase Auth responde a credenciales equivocadas o email sin confirmar:
// son respuestas normales del flujo, no fallas del sistema.
const esCredencialInvalida = (detalle: string): boolean =>
  /invalid login credentials|invalid_credentials|email not confirmed|credenciales/i.test(detalle);

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Por favor ingrese email y contraseña');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err: unknown) {
      // Credenciales equivocadas es lo esperable acá: se muestra y no se reporta, o Sentry
      // se llenaría de contraseñas mal tecleadas. Cualquier otra falla sí se reporta:
      // un login roto sin aviso deja a la persona afuera sin que nadie se entere.
      const detalle = detalleTecnico(err);
      if (esCredencialInvalida(detalle)) {
        setError('Email o contraseña incorrectos. Verifica tus datos e intenta de nuevo.');
      } else {
        setError('No pudimos iniciar sesión. Ya avisamos al equipo.');
        reportarErrorInesperado(err, 'No pudimos iniciar sesión.', { email_intentado: email });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex flex-col items-center mb-8">
            <div className="bg-blue-600 p-4 rounded-full mb-4">
              <HeartHandshake className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 text-center">
              Gestión Administrativa Gnóstica
            </h1>
            <p className="text-slate-600 text-center mt-2">
              Ingrese sus credenciales para continuar
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all"
                  placeholder="usuario@ejemplo.com"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all"
                  placeholder="••••••••"
                  disabled={loading}
                />
              </div>
            </div>

            <FormErrorAlert error={error} />

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Iniciando sesión...</span>
                </div>
              ) : (
                'Iniciar Sesión'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">
              Si necesita ayuda para ingresar, consulte al personal a cargo de su sede.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
